import { CampaignDetails, EntityInfo, LocaleSettings, OrganizationContext } from '@mmt/events-schema'
import { Org, OrgValue } from '../../constants'

export function isBrowserEnv() {
  return typeof window !== 'undefined'
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

// Drops null, undefined, "", and "NA", including nested objects whose fields are all invalid.
// A shallow Object.keys check keeps { user_role: undefined } because the keys exist;
// JSON.stringify then emits organization_context: {}.
export function filterValidValues<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const result: Partial<T> = {}
  for (const key in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) {
      continue
    }

    const value = obj[key]
    if (value === null || value === undefined || value === 'NA' || value === '') {
      continue
    }

    if (Array.isArray(value)) {
      if (value.length > 0) {
        result[key] = value as T[Extract<keyof T, string>]
      }
      continue
    }

    if (isPlainObject(value)) {
      const nested = filterValidValues(value)
      if (Object.keys(nested).length > 0) {
        result[key] = nested as T[Extract<keyof T, string>]
      }
      continue
    }

    result[key] = value
  }
  return result
}

export type LoginInfo = {
  loginId: string
  loginType: string
  countryCode: string
  verified: boolean
  commId?: string
  status: string
}

interface UserCorpData {
  agencyUUID: string | null
  agentRole: string | null
  organizationId: string | null
  orgType: string | null
  employeeStatus: string | null
  employeeType: string | null
}

interface UserIdentities {
  mHash: string | null
  eHash: string | null
}
interface UserUid {
  uuid: string | null
  profileType: string | null
  phoneNumberHash: string | null
  emailHash: string | null
  nationality: string | null
  loginInfoList: LoginInfo[]
  userCorpData: UserCorpData
  identities: UserIdentities
  value: UserIdentities
}

export function readCookieValue(cookieString: string, cookieName: string): string | null {
  // Split the cookie string into individual cookies
  const cookies = cookieString.split('; ')

  // Loop through each cookie
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i]

    // If the cookie name matches, return the cookie value
    if (cookie.indexOf(cookieName + '=') === 0) {
      return cookie.substring(cookieName.length + 1)
    }
  }

  // If the cookie was not found, return null
  return null
}

function xorDecrypt(ciphertext: string, key: string): string | null {
  try {
    let plaintext = ''
    let keyIndex = 0
    if (!key) throw new Error('Key is empty')
    for (let i = 0; i < ciphertext.length; i += 1) {
      const charCode = ciphertext.charCodeAt(i)
      const keyChar = key.charCodeAt(keyIndex)
      // eslint-disable-next-line no-bitwise
      const decryptedCharCode = charCode ^ keyChar
      plaintext += String.fromCharCode(decryptedCharCode)
      keyIndex = (keyIndex + 1) % key.length
    }
    return plaintext
  } catch (err) {
    console.error('An error occurred during the decryption process:', err)
    return null
  }
}

export function getUserUid(isNotEncrypted?: boolean): UserUid | null {
  const itemVal: string | null = sessionStorage.getItem('userInfo') || localStorage.getItem('userInfo')
  if (!itemVal) {
    console.log('userInfo is not found in sessionStorage')
    return null
  }
  if (isNotEncrypted) {
    return JSON.parse(itemVal)
  }
  const prefixRegex = new RegExp(`^enct-`)
  const cleanedItemVal = itemVal.replace(prefixRegex, '')
  const decryptedVal = xorDecrypt(cleanedItemVal, 'awefewfuihewfhewfuhiebc')
  if (!decryptedVal) {
    console.error('Decryption failed')
    return null
  }
  try {
    const encryptedVal = JSON.parse(decryptedVal)
    return encryptedVal // Assuming encryptedVal is a string. If not, you may need to adjust this.
  } catch (e) {
    console.error('Error parsing JSON:', e)
    return null
  }
}

export function getMarketingCloudVisitorID(): string {
  const localStorageKey = 'marketingCloudVisitorID'
  const windowVisitorID = window?.s?.marketingCloudVisitorID
  const localStorageVisitorID = localStorage.getItem(localStorageKey)

  const cookieValue = readCookieValue(document?.cookie, 's_ecid')
  if (cookieValue) {
    const decodedCookieValue = decodeURI(cookieValue)
    return decodedCookieValue?.split('|')[1]
  }

  if (windowVisitorID) {
    if (windowVisitorID != localStorageVisitorID) {
      localStorage.setItem(localStorageKey, windowVisitorID)
    }
    return windowVisitorID
  }

  if (localStorageVisitorID) {
    return localStorageVisitorID
  }

  return 'NA'
}

export function getUserFromStorage(org?: string): UserUid | null {
  return getUserUid(org === OrgValue.GI)
}

type UserContext = {
  uuid?: string | null
  profile_type?: string | null
}

// Utility method to return userUUIDPt
export function getUserUUIDPt(userContext?: UserContext): unknown {
  if (!userContext) {
    return null
  }
  const { uuid = '', profile_type = 'PERSONAL' } = userContext

  if (profile_type && uuid) {
    return `${uuid}/${profile_type.toUpperCase()}`
  }
  return null
}

export function getRequestQueryPrem(key?: string): string | undefined {
  const queryString = window.location.search.substring(1)
  const queryParams = queryString.split('&')

  for (let i = 0; i < queryParams.length; i++) {
    const pair = queryParams[i].split('=')
    const paramName = decodeURIComponent(pair[0])
    const paramValue = decodeURIComponent(pair[1])
    if (paramName === key) {
      return paramValue // Return the value if found
    }
  }
}

export function getCampaignDetails(): CampaignDetails | null {
  const getCMPName = getRequestQueryPrem('cmp')
  if (!getCMPName) {
    return null
  }
  return {
    campaign_key: getCMPName,
  }
}

export function updateDomain(url?: string): string {
  if (url) {
    if (!/^https?:\/\//i.test(url)) {
      return `https://${url}`
    }
    return url
  }

  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  const baseDomain = hostname?.split('.')?.slice(-2)?.join('.') || 'makemytrip.com'

  return `https://pdt.${baseDomain}`
}

type ReqHeaders = Record<string, string>
export function updateHeaders(headers: ReqHeaders, url: string): ReqHeaders {
  const currentDomain = url || (typeof window !== 'undefined' ? window.location.hostname : '')
  let additionalHeader = {}
  if (isMakeMyTripDomain(currentDomain)) {
    additionalHeader = {
      'entity-name': currentDomain && currentDomain.includes('.global') ? 'global' : 'india',
    }
  }
  return {
    ...headers,
    ...additionalHeader,
  }
}

const CURRENCY_MAP = {
  in: 'inr',
  ae: 'aed',
  sa: 'sar',
}

const DOMAIN_SETTINGS: Record<string, LocaleSettings> = {
  'sa.makemytrip': {
    currency: 'sar',
    isWorldwide: 1,
    language: 'eng',
    region: 'sa',
    userCountry: 'sa',
    userCurrency: 'sar',
    entityName: 'global',
  },
  'ae.makemytrip': {
    currency: 'aed',
    isWorldwide: 0,
    language: 'eng',
    region: 'ae',
    userCountry: 'ae',
    userCurrency: 'aed',
    entityName: 'global',
  },
  '.global': {
    currency: 'aed',
    isWorldwide: 1,
    language: 'eng',
    region: 'ae',
    userCountry: 'ae',
    userCurrency: 'aed',
    entityName: 'global',
  },
}

const DEFAULT_SETTINGS: LocaleSettings = {
  currency: 'inr',
  isWorldwide: 0,
  language: 'eng',
  region: 'in',
  userCountry: 'in',
  userCurrency: 'inr',
  entityName: 'india',
}

const getFallbackSettings = (): LocaleSettings => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  const matchedKey = Object.keys(DOMAIN_SETTINGS).find(key => hostname.includes(key))
  return matchedKey ? DOMAIN_SETTINGS[matchedKey] : DEFAULT_SETTINGS
}

export function isGoibiboDomain(): boolean {
  const currentDomain = typeof window !== 'undefined' ? window.location.hostname : ''
  return currentDomain.includes('goibibo')
}

export function isMakeMyTripDomain(domain?: string): boolean {
  const currentDomain = domain || (typeof window !== 'undefined' ? window.location.hostname : '')
  return currentDomain.includes('makemytrip')
}

export function getEntityInfoForGI(): EntityInfo {
  return {
    entity: 'india',
    currency: 'inr',
    region: 'in',
    user_language: 'eng',
    organisation: 'gi',
  }
}

export function getEntityInfoForMMT(localeData?: LocaleSettings): EntityInfo {
  const fallback = getFallbackSettings()
  const { region, userCountry, language, userCurrency, currency, isWorldwide } = getLocaleSettings(localeData)
  const entity = localeData?.entityName ?? fallback.entityName ?? 'india'

  return {
    entity,
    currency,
    region,
    user_currency: userCurrency,
    user_country: isWorldwide ? userCountry : region,
    user_language: language,
    organisation: 'mmt',
  }
}

export function getEntityInfo(localeData?: LocaleSettings): EntityInfo {
  if (isGoibiboDomain()) {
    return getEntityInfoForGI()
  }
  return getEntityInfoForMMT(localeData)
}

export function getLocaleSettings(localeData?: LocaleSettings): LocaleSettings {
  const fallback = getFallbackSettings()

  // Read cookie values
  const cookies = {
    userCountry: readCookieValue(document?.cookie, 'cc'),
    region: readCookieValue(document?.cookie, 'ccde'),
    isWW: readCookieValue(document?.cookie, 'isWW'),
    language: readCookieValue(document?.cookie, 'lang'),
    userCurrency: readCookieValue(document?.cookie, 'userCurrency'),
  }

  // Priority: cookie > localeData > fallback
  const region = (cookies.region ?? localeData?.region ?? fallback.region).toLowerCase()
  const userCountry = (cookies.userCountry ?? localeData?.userCountry ?? fallback.userCountry).toLowerCase()
  const language = (cookies.language ?? localeData?.language ?? fallback.language).toLowerCase()
  const userCurrency = (cookies.userCurrency ?? localeData?.userCurrency ?? fallback.userCurrency).toLowerCase()
  const isWorldwide = cookies.isWW != null ? parseInt(cookies.isWW) : localeData?.isWorldwide ?? fallback.isWorldwide
  const currency = (
    localeData?.currency ??
    CURRENCY_MAP[region as keyof typeof CURRENCY_MAP] ??
    fallback.currency
  ).toLowerCase()

  return { currency, isWorldwide, language, region, userCountry, userCurrency }
}

export const PROFILE_TYPE_MAP = {
  b2b: 'business',
  b2c: 'personal',
  myPartner: 'cta',
}

export function getProfileType(): string {
  const myBusinessSubscription = readCookieValue(document?.cookie, 'myBusinessSubscription')
  return PROFILE_TYPE_MAP[myBusinessSubscription as keyof typeof PROFILE_TYPE_MAP] || PROFILE_TYPE_MAP.b2c
}

export function isGI(org: Org): boolean {
  return org === OrgValue.GI
}

export function parsePipeDelimitedString(str: string): Record<string, string> {
  if (!str || typeof str !== 'string') {
    return {}
  }

  const result: Record<string, string> = {}
  const pairs = str.split('|')

  pairs.forEach(pair => {
    const [key, value] = pair.split(':')
    if (key && value !== undefined) {
      result[key.trim()] = value.trim()
    }
  })

  return result
}

export function getOrganizationContext(): OrganizationContext {
  const profileType = getProfileType()
  const mmtUserDet = getUserFromStorage()
  if (profileType === PROFILE_TYPE_MAP.myPartner) {
    return {
      user_role: mmtUserDet?.userCorpData?.agentRole,
      org_id: mmtUserDet?.userCorpData?.agencyUUID?.toString(),
    }
  }

  if (profileType === PROFILE_TYPE_MAP.b2b) {
    const orgContext = localStorage.getItem('myBizOrgContext') || ''
    if (!orgContext) {
      return {}
    }
    const parsedOrgContext = parsePipeDelimitedString(orgContext)
    return {
      org_id: mmtUserDet?.userCorpData?.organizationId?.toString(),
      org_type: mmtUserDet?.userCorpData?.orgType,
      verification_status: mmtUserDet?.userCorpData?.employeeStatus,
      user_role: mmtUserDet?.userCorpData?.employeeType,
      ...parsedOrgContext,
    }
  }

  return {}
}
